import {Tabs} from 'antd';
import {useMemo} from 'react';

import {BarChart} from '../../layout/chart/bar-chart/bar-chart';
import {Spinner} from '../../layout/spinner/spinner';
import {WeekDaysEnum} from '../../provider/locale/locale-context-const';
import {useLocale} from '../../provider/locale/locale-hook';
import {LangKeyType} from '../../provider/locale/translation/type';
import {getEnumValueEnsure} from '../../util/enum';
import {getRandomString} from '../../util/string';
import {AlertFallback} from '../alert-fallback/alert-fallback';
import {Text} from '../text/text';

import {WorkloadEmpty} from './workload-empty';
import {
    deserializeDataForChartByWeekDay,
    getWorkloadChartOptions,
    WEEK_DAY_TO_LOCALIZED_STRING,
} from './workload-helper';
import {WorkloadByHoursAndWeekDaysType} from './workload-type';
import * as styles from './workload.scss';

type PropsType = {
    workloadData?: WorkloadByHoursAndWeekDaysType | null;
    isInProgress: boolean;
    isEmpty: boolean;
    emptyLangKey: LangKeyType;
    hasProcessError?: boolean;
    footerLangKey?: LangKeyType;
    yMax?: number;
};

export function Workload(props: PropsType): JSX.Element {
    const {workloadData: data, isEmpty, isInProgress, hasProcessError, emptyLangKey, footerLangKey, yMax = 100} = props;
    const {getLocalizedString} = useLocale();

    const tabs = useMemo(
        () =>
            Object.entries(WEEK_DAY_TO_LOCALIZED_STRING).map(([key, weekDayLangKey]: [unknown, LangKeyType]) => {
                const dayName = getLocalizedString(weekDayLangKey);

                const dayData = deserializeDataForChartByWeekDay(
                    data || {},
                    getEnumValueEnsure<WeekDaysEnum>(WeekDaysEnum, key, WeekDaysEnum.monday)
                );

                return {
                    key: String(getRandomString()),
                    label: dayName,
                    children: dayData.datasets.some((dataset) => dataset.values.some(Boolean)) ? (
                        <div className={styles.Workload_barChartContainer}>
                            <BarChart
                                data={dayData}
                                options={getWorkloadChartOptions(
                                    {
                                        data: {
                                            labels: dayData.labels,
                                            datasets: dayData.datasets,
                                        },
                                        tooltipTitle: dayName,
                                    },
                                    yMax
                                )}
                            />
                        </div>
                    ) : (
                        <WorkloadEmpty textKey={emptyLangKey} />
                    ),
                };
            }),
        [data, emptyLangKey, getLocalizedString, yMax]
    );

    if (hasProcessError) {
        return <AlertFallback />;
    }

    if (isInProgress) {
        return (
            <div className={styles.Workload_loadingContainer}>
                <Spinner />
            </div>
        );
    }

    if (isEmpty) {
        return <WorkloadEmpty textKey={emptyLangKey} />;
    }

    return (
        <div className={styles.Workload}>
            <div className={styles.Workload_chartContainer}>
                <Tabs items={tabs} />
            </div>
            {footerLangKey && <Text block secondary small stringKey={footerLangKey} />}
        </div>
    );
}
